import React, {useState, useCallback} from 'react';
import {GatewayGetSchema} from '../../../../../api/DBSchema/Gateway';
import EditorLayout from '../../../../../components/Layouts/EditorLayout';
import {useTranslation} from 'react-i18next';
import SMCTable from '../../../../../components/core/SMCTable/SMCTable';
import ButtonContainer from '../../../../../components/Simple/ButtonContainer';
import {useBoolean} from '../../../../../utils/hooks';
import SMCButton from '../../../../../components/Simple/SMCButton';
import SMCLabel from '../../../../../components/Simple/SMCLabel';
import SMCLink from '../../../../../components/Simple/SMCLink';
import AdminAccessEditorModal from '../../../../PopupPages/AdminAccessEditorModal';

const pageSchema = GatewayGetSchema.pages.administratorAccess;
const pageId = pageSchema.pageId;

function BaseAdministratorAccess({
    pageData,
    setObjectData,
    pageDisabled
}) {
    const { t } = useTranslation();

    const setPageData = useCallback((paramName, newValue) => {
        if (!pageDisabled) {
            setObjectData([pageId, paramName], newValue);
        }
    }, [setObjectData, pageDisabled])

    const adminAccessDataList = pageData[pageSchema.adminAccessDataList];    
    const adminAccessSchema   = pageSchema.adminAccessDataListSchema;

    const [isNewAdministratorAccessWindowOpen, openNewAdministratorAccessWindow, closeNewAdministratorAccessWindow] = useBoolean(false);
    const [selectedObjects, setSelectedObjects] = useState([]);
    const [editObject, setEditObject] = useState(null);
    const [editObjectKey, setEditObjectKey] = useState(null);

    const deleteSelectedAdminAccess = useCallback(() => {
        if (adminAccessDataList && selectedObjects.length > 0) {
            const newAdminAccessDataList = adminAccessDataList.filter((e, i) => (!selectedObjects.includes(i)));
            setSelectedObjects([]);
            setPageData(pageSchema.adminAccessDataList, newAdminAccessDataList);
        }
    }, [adminAccessDataList, selectedObjects, setPageData]);

    const adminAccessEditorOnCancel = useCallback(() => {
        // Set edit object and edit object key to null, to signify 
        setEditObject(null);
        setEditObjectKey(null);
        closeNewAdministratorAccessWindow();
    }, [closeNewAdministratorAccessWindow]);

    // Callback for when admin access editor is used for a new object
    const newAdminAccessCallback = useCallback(newAdminAccessObject => {
        const adminAccessDataListCopy = adminAccessDataList ? [...adminAccessDataList] : [];
        adminAccessDataListCopy.push({
            [adminAccessSchema.type]: newAdminAccessObject.type,
            [adminAccessSchema.ipAddr]: newAdminAccessObject.ipAddr,
            [adminAccessSchema.subnetMask]: newAdminAccessObject.subnetMask
        });
        setPageData(pageSchema.adminAccessDataList, adminAccessDataListCopy);
        closeNewAdministratorAccessWindow();
    }, [adminAccessDataList, adminAccessSchema, closeNewAdministratorAccessWindow, setPageData]);

    // Callback for when admin access editor is used to edit an existing object
    const editAdminAccessCallback = useCallback(editedAdminAccessObject => {
        const adminAccessDataListCopy = [...adminAccessDataList];
        adminAccessDataListCopy[editObjectKey] = {
            [adminAccessSchema.type]: editedAdminAccessObject.type,
            [adminAccessSchema.ipAddr]: editedAdminAccessObject.ipAddr,
            [adminAccessSchema.subnetMask]: editedAdminAccessObject.subnetMask
        };
        setPageData(pageSchema.adminAccessDataList, adminAccessDataListCopy);
        setEditObject(null);
        setEditObjectKey(null);
        closeNewAdministratorAccessWindow();
    }, [adminAccessDataList, adminAccessSchema, editObjectKey, closeNewAdministratorAccessWindow, setPageData]);

    const IpAddressCell = ({value, object, index}) => {
        return (
            <SMCLink
                inline
                disabled={pageDisabled}
                onClick={() => {
                    setEditObject(object);
                    setEditObjectKey(index);
                    openNewAdministratorAccessWindow();
                }}
            >
                { value }
            </SMCLink>
        );
    }

    const TypeCell = ({value}) => {
        if (value === adminAccessSchema.typeValues.ipAddress) {
            return t('SMP.PLAN.ADMIN_ACCESS_WIZ.IP_ADDRESS');
        } else if (value === adminAccessSchema.typeValues.network) {
            return t('SMP.NETWORK');
        }
        return value;
    }

    return (
        <EditorLayout>
            <SMCLabel bold>{t('SMP.PLAN.SERVICES.ADMIN_ACCESS.LINE1')}</SMCLabel>
            <ButtonContainer>
                <SMCButton variant="ghost" disabled={pageDisabled} icon="new" onClick={openNewAdministratorAccessWindow}>{t('SMC_GEN_NEW')}</SMCButton>
                <SMCButton variant="ghost" disabled={pageDisabled} icon="delete" onClick={deleteSelectedAdminAccess}>{t('SMC_GEN_DELETE')}</SMCButton>
            </ButtonContainer>
            <SMCTable
                columns={[
                    {id: 'ip_address',  accessor: adminAccessSchema.ipAddr,     header: t('SMP.PLAN.SERVICES.ADMIN_ACCESS.IP_ADDRESS_NET'), width: 130, Cell: IpAddressCell },
                    {id: 'subnet_mask', accessor: adminAccessSchema.subnetMask, header: t('SMP.PLAN.SERVICES.ADMIN_ACCESS.SUBNET_MASK'),    width: 130,                     },
                    {id: 'type',        accessor: adminAccessSchema.type,       header: t('SMP.PLAN.SERVICES.ADMIN_ACCESS.TYPE'),           width: 130, Cell: TypeCell      }
                ]}
                rowIdSelector={(obj, i) => (i)}
                selectedObjects={selectedObjects}
                setSelectedObjects={setSelectedObjects}
                content={adminAccessDataList}
                hasCheckAll={true}
                width={530}
                maxHeight={500}
            />
            <AdminAccessEditorModal
                isOpen={isNewAdministratorAccessWindowOpen}
                onCancel={adminAccessEditorOnCancel}
                // If editObject is set, the callback is editor callback, otherwise, we are creating a new object
                onSuccess={editObject ? editAdminAccessCallback : newAdminAccessCallback}
                defaultObject={editObject}
            />
        </EditorLayout>
    );
}

export default BaseAdministratorAccess;